/**
 * SEO mega nav behavior.
 *
 * Desktop ([data-seo-nav]):
 *  - open on hover with a ~120ms intent delay, and on click / Enter / Space
 *  - aria-expanded / aria-controls kept in sync, only one panel open
 *  - Escape closes and returns focus to the trigger
 *  - clicking outside closes; Tab moves through panel links in DOM order
 *  - 150ms fade handled in CSS; prefers-reduced-motion respected in CSS
 *
 * Mobile drawer ([data-seo-drawer]):
 *  - accordion rows are <button aria-expanded> controlling a region
 *  - one row open at a time
 */
(() => {
  const HOVER_OPEN_DELAY = 120;
  const HOVER_CLOSE_DELAY = 160;

  function initDesktop(nav) {
    const triggers = Array.from(nav.querySelectorAll('[data-seo-trigger]'));
    const dim = nav.querySelector('[data-seo-dim]');
    if (!triggers.length) return;

    let openTrigger = null;
    let hoverTimer = null;

    const panelFor = (trigger) => document.getElementById(trigger.getAttribute('aria-controls'));

    function show(el) {
      el.hidden = false;
      // Force a frame so the fade transition runs from opacity 0.
      requestAnimationFrame(() => el.classList.add('is-open'));
    }

    function hide(el) {
      el.classList.remove('is-open');
      el.hidden = true;
    }

    function openPanel(trigger) {
      if (openTrigger === trigger) return;
      if (openTrigger) closePanel({ keepDim: true });
      const panel = panelFor(trigger);
      if (!panel) return;
      trigger.setAttribute('aria-expanded', 'true');
      show(panel);
      if (dim) show(dim);
      openTrigger = trigger;
    }

    function closePanel({ returnFocus = false, keepDim = false } = {}) {
      if (!openTrigger) return;
      const trigger = openTrigger;
      const panel = panelFor(trigger);
      trigger.setAttribute('aria-expanded', 'false');
      if (panel) hide(panel);
      if (dim && !keepDim) hide(dim);
      openTrigger = null;
      if (returnFocus) trigger.focus();
    }

    triggers.forEach((trigger) => {
      const item = trigger.closest('.seo-nav__item');

      // Click / Enter / Space toggles the panel instead of following the link.
      // The same destination is linked inside the panel, and the href stays in
      // the server-rendered HTML for crawlers.
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        clearTimeout(hoverTimer);
        if (openTrigger === trigger) {
          closePanel();
        } else {
          openPanel(trigger);
        }
      });

      if (!item) return;

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => openPanel(trigger), HOVER_OPEN_DELAY);
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(hoverTimer);
        if (openTrigger !== trigger) return;
        hoverTimer = setTimeout(() => {
          if (openTrigger === trigger) closePanel();
        }, HOVER_CLOSE_DELAY);
      });
    });

    // Hovering a non-panel top-level item closes any open panel.
    nav.querySelectorAll('.seo-nav__item').forEach((item) => {
      if (item.querySelector('[data-seo-trigger]')) return;
      item.addEventListener('pointerenter', () => {
        clearTimeout(hoverTimer);
        closePanel();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openTrigger) {
        closePanel({ returnFocus: true });
      }
    });

    document.addEventListener('click', (event) => {
      if (openTrigger && !nav.contains(event.target)) closePanel();
    });

    // Close when keyboard focus leaves the nav entirely (no focus trap).
    nav.addEventListener('focusout', (event) => {
      if (openTrigger && event.relatedTarget && !nav.contains(event.relatedTarget)) {
        closePanel();
      }
    });

    if (dim) dim.addEventListener('click', () => closePanel());
  }

  function initDrawer(drawer) {
    const buttons = Array.from(drawer.querySelectorAll('[data-seo-acc]'));

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const region = document.getElementById(button.getAttribute('aria-controls'));
        const isOpen = button.getAttribute('aria-expanded') === 'true';

        buttons.forEach((other) => {
          other.setAttribute('aria-expanded', 'false');
          const otherRegion = document.getElementById(other.getAttribute('aria-controls'));
          if (otherRegion) otherRegion.hidden = true;
        });

        if (!isOpen) {
          button.setAttribute('aria-expanded', 'true');
          if (region) region.hidden = false;
        }
      });
    });
  }

  function init() {
    const nav = document.querySelector('[data-seo-nav]');
    if (nav && !nav.dataset.seoNavReady) {
      nav.dataset.seoNavReady = 'true';
      initDesktop(nav);
    }

    document.querySelectorAll('[data-seo-drawer]').forEach((drawer) => {
      if (drawer.dataset.seoDrawerReady) return;
      drawer.dataset.seoDrawerReady = 'true';
      initDrawer(drawer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after the theme editor re-renders sections.
  document.addEventListener('shopify:section:load', init);
})();
